import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ForumAPI } from '../utils/api';
import { getToken } from '../utils/auth';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const EditForumPostPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState('announcement');
    const [isPinned, setIsPinned] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [imageUrl, setImageUrl] = useState('');
    const [previewBaseUrl, setPreviewBaseUrl] = useState('');

    const categories = [
        { value: 'bqt', label: 'Thông Báo Từ BQT' },
        { value: 'bcs', label: 'Góc Học Tập - Ban Cán Sự' },
        { value: 'general', label: 'Thảo Luận Chung' }
    ];

    React.useEffect(() => {
        import('../utils/api').then(({ getApiBaseUrl }) => {
            const apiUrl = getApiBaseUrl();
            setPreviewBaseUrl(apiUrl.split('/api')[0]);
        });
    }, []);

    const fullPreviewUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${previewBaseUrl}${imageUrl}`) : '';

    useEffect(() => {
        loadNews();
    }, [id]);

    const loadNews = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token || !id) {
                navigate('/login');
                return;
            }

            const response = await ForumAPI.getById(id, token);
            setTitle(response.title);
            setContent(response.content);
            setCategory(response.category);
            setIsPinned(response.isPinned);
            setImageUrl(response.image || '');
        } catch (error: any) {
            console.error('Load news error:', error);
            toast.error('Không thể tải tin tức');
            navigate('/forum');
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Chỉ chấp nhận file ảnh');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Ảnh tối đa 5MB');
            return;
        }

        try {
            const token = getToken();
            if (!token) return;

            toast.loading('Đang tải ảnh lên...', { id: 'upload-img' });
            const res = await ForumAPI.uploadImage(file, token);
            setImageUrl(res.url);
            toast.success('Đã tải ảnh lên', { id: 'upload-img' });
        } catch (error) {
            toast.error('Lỗi tải ảnh', { id: 'upload-img' });
        }
    };

    const removeImage = () => setImageUrl('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title.trim() || !content.trim()) {
            toast.error('Vui lòng điền đầy đủ thông tin');
            return;
        }

        try {
            setSubmitting(true);
            const token = getToken();
            if (!token || !id) {
                navigate('/login');
                return;
            }

            await ForumAPI.update(id, { title, content, category, isPinned, image: imageUrl }, token);
            toast.success('Đã cập nhật tin tức thành công!');
            navigate(`/forum/${id}`);
        } catch (error: any) {
            console.error('Update news error:', error);
            toast.error(error.message || 'Không thể cập nhật tin tức');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-500">Đang tải...</div>
            </div>
        );
    }

    return (
        <>
            <div className="relative overflow-hidden group bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl animate-slideDownIn">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>
                <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-transparent via-white/40 to-transparent blur-[2px] animate-[shimmer_3s_ease-in-out_infinite] [mask-image:linear-gradient(to_right,transparent_0%,black_20%,black_80%,transparent_100%)] mix-blend-overlay pointer-events-none"></div>
                <div className="max-w-screen-2xl mx-auto p-8 sm:p-12">
                    <div className="relative z-10">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-white mb-4 tracking-tight">Chỉnh sửa tin tức</h1>
                        <p className="text-base font-mono sm:text-lg text-blue-100 dark:text-blue-200 max-w-2xl leading-relaxed">Cập nhật nội dung tin tức</p>
                    </div>
                </div>
            </div>
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 animate-fadeIn">

                {/* 2-Column Layout: Form + Preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* LEFT: Form Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-slideUpIn">
                        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">

                            {/* Image Upload Section */}
                            <div className="space-y-3">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                                    Ảnh bìa (Không bắt buộc)
                                </label>
                                {!imageUrl ? (
                                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-all relative cursor-pointer group">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        />
                                        <div className="text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors">
                                            <svg className="w-10 h-10 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                            <p className="font-semibold text-sm">Nhấn để tải ảnh bìa</p>
                                            <p className="text-xs mt-1 opacity-75">PNG, JPG, GIF (Max 5MB)</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-video max-h-[200px] group shadow-inner">
                                        <img
                                            src={fullPreviewUrl}
                                            alt="Cover"
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button
                                                type="button"
                                                onClick={removeImage}
                                                className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg backdrop-blur-md transition-all font-medium border border-white/20 text-sm"
                                            >
                                                Xóa ảnh
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Title & Category */}
                            <div className="grid grid-cols-1 gap-6">
                                <div className="space-y-3">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                                        Tiêu đề bài viết
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:bg-white dark:focus:bg-gray-900 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 transition-all outline-none font-semibold text-gray-800 dark:text-white"
                                        required
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                                        Danh mục
                                    </label>
                                    <select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:bg-white dark:focus:bg-gray-900 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 transition-all outline-none font-semibold text-gray-800 dark:text-white"
                                    >
                                        {categories.map((cat) => (
                                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="space-y-3">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                                    Nội dung chi tiết
                                </label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:bg-white dark:focus:bg-gray-900 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 transition-all outline-none resize-y min-h-[300px] text-gray-800 dark:text-gray-100 leading-relaxed"
                                    required
                                />
                            </div>

                            {/* Footer Actions */}
                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-4">
                                <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setIsPinned(!isPinned)}>
                                    <div className={`w-12 h-7 rounded-full transition-colors relative ${isPinned ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}>
                                        <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${isPinned ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                    </div>
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Ghim tin tức này</span>
                                </div>

                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/forum/${id}`)}
                                        className="flex-1 px-6 py-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-bold text-sm transition-colors border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        Hủy bỏ
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                    >
                                        {submitting ? 'Đang cập nhật...' : 'Cập nhật'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* RIGHT: Preview Card - Same as CreateNewsPage */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-slideUpIn sticky top-6 h-fit">
                        <div className="border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Xem trước</h2>
                        </div>

                        <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                            {imageUrl && fullPreviewUrl && (
                                <div className="w-full aspect-video bg-gray-100 dark:bg-gray-900">
                                    <img src={fullPreviewUrl} alt="Preview" className="w-full h-full object-cover" />
                                </div>
                            )}

                            <div className="px-6 pb-6 pt-4 space-y-4">
                                <div className="flex items-center gap-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${category === 'bqt' ? 'bg-red-500' :
                                        category === 'bcs' ? 'bg-blue-500' : 'bg-green-500'
                                        }`}>
                                        {categories.find(c => c.value === category)?.label}
                                    </span>
                                    {isPinned && (
                                        <span className="px-2 py-1 rounded-full bg-red-50 text-red-600 text-xs font-bold border border-red-100">
                                            📌 Ghim
                                        </span>
                                    )}
                                </div>

                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                                    {title || <span className="text-gray-400 italic">Tiêu đề...</span>}
                                </h3>

                                <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                                    {content ? (
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeRaw]}
                                        >
                                            {content}
                                        </ReactMarkdown>
                                    ) : (
                                        <span className="text-gray-400 italic">Nội dung...</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default EditForumPostPage;
